import { jsx as _jsx } from "react/jsx-runtime";
// @ts-expect-error module is untyped
import FormattedDuration from 'I18n/components/FormattedDuration';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import unescapedText from 'I18n/utils/unescapedText';
import UILink from 'UIComponents/link/UILink';
import * as AssetUninstallableReason from '../constants/AssetUninstallableReason';
import { isFailed, isFulfilled } from '../../utils/requestState';
import { getAppShortlink, getCmsFreeSignupUrl, getCmsHubPricingUrl, getDownloadedAssetsUpdateUrl, getDownloadedAssetsUrl, getMarketingHubPricingUrl } from '../utils/urls';
const ONE_MINUTE = 60 * 1000;
export function useAssetInstallabilityMessage({
  assetUninstallableReason,
  installability,
  offeringId,
  offeringSlug,
  offeringType,
  portalId,
  requestState
}) {
  const {
    installed,
    canUpdate,
    pendingPaymentExpiry
  } = installability || {};

  if (!isFulfilled(requestState)) {
    return {
      hasAccess: false
    };
  }

  if (isFailed(requestState)) {
    return {
      hasAccess: false,
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ecosystemMarketplaceCore.themes.installability.installabilityRequestFailed",
        options: {
          offeringType: unescapedText(`ecosystemMarketplaceCore.offeringType.${offeringType}_lowercase`)
        }
      })
    };
  }

  const hasAlreadyInstalledReason = assetUninstallableReason === AssetUninstallableReason.ALREADY_INSTALLED;

  if (installed && canUpdate && hasAlreadyInstalledReason) {
    return {
      hasAccess: true,
      message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "ecosystemMarketplaceCore.themes.installability.updateAvailable_jsx",
        elements: {
          Link: UILink
        },
        options: {
          downloadsLink: {
            href: getDownloadedAssetsUpdateUrl(offeringId, offeringType)
          },
          offeringType: unescapedText(`ecosystemMarketplaceCore.offeringType.${offeringType}_lowercase`)
        }
      })
    };
  }

  switch (assetUninstallableReason) {
    case AssetUninstallableReason.FORBIDDEN_ADMIN_PREVIEW:
    case AssetUninstallableReason.FORBIDDEN_PUBLIC:
    case AssetUninstallableReason.ALREADY_INSTALLED:
      return {
        hasAccess: false
      };

    case AssetUninstallableReason.PROVIDER_OFFERING:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "ecosystemMarketplaceCore.themes.installability.providerAsset_jsx",
          elements: {
            Link: UILink
          },
          options: {
            anotherAccountLink: {
              href: getAppShortlink({
                pathname: `/website/${offeringSlug}`,
                theme: offeringSlug
              }, false)
            },
            signupLink: {
              href: getCmsFreeSignupUrl(offeringSlug)
            }
          }
        })
      };

    case AssetUninstallableReason.EXCEEDS_ASSET_LIMIT:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystemMarketplaceCore.themes.installability.exceedsAssetLimit",
          options: {
            offeringType: unescapedText(`ecosystemMarketplaceCore.offeringType.${offeringType}_lowercase`)
          }
        })
      };

    case AssetUninstallableReason.PURCHASED_NOT_INSTALLED:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "ecosystemMarketplaceCore.themes.installability.purchasedNotInstalled_jsx",
          elements: {
            Link: UILink
          },
          options: {
            downloadsLink: {
              href: getDownloadedAssetsUrl(false, offeringType)
            },
            offeringType: unescapedText(`ecosystemMarketplaceCore.offeringType.${offeringType}_lowercase`)
          }
        })
      };

    case AssetUninstallableReason.PENDING_PAYMENT:
      {
        const duration = /*#__PURE__*/_jsx(FormattedDuration, {
          from: new Date().valueOf(),
          to: pendingPaymentExpiry + ONE_MINUTE
        });

        return {
          hasAccess: false,
          message: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "ecosystemMarketplaceCore.themes.installability.paymentPendingExpiry",
            options: {
              duration,
              offeringType: unescapedText(`ecosystemMarketplaceCore.offeringType.${offeringType}_lowercase`)
            }
          })
        };
      }

    case AssetUninstallableReason.PENDING_INSTALL:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "ecosystemMarketplaceCore.themes.installability.pendingInstall_jsx",
          elements: {
            Link: UILink
          },
          options: {
            downloadsLink: {
              href: getDownloadedAssetsUrl(false, offeringType)
            },
            offeringType: unescapedText(`ecosystemMarketplaceCore.offeringType.${offeringType}_lowercase`)
          }
        })
      };

    case AssetUninstallableReason.PENDING_UNINSTALL:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "ecosystemMarketplaceCore.themes.installability.pendingUninstall_jsx",
          elements: {
            Link: UILink
          },
          options: {
            downloadsLink: {
              href: getDownloadedAssetsUrl(false, offeringType)
            },
            offeringType: unescapedText(`ecosystemMarketplaceCore.offeringType.${offeringType}_lowercase`)
          }
        })
      };

    case AssetUninstallableReason.MISSING_REQUIRED_SUBSCRIPTION:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "ecosystemMarketplaceCore.themes.installability.missingRequiredSubscription_jsx",
          elements: {
            Link: UILink
          },
          options: {
            cmsHubLink: {
              external: true,
              href: getCmsHubPricingUrl()
            },
            marketingHubLink: {
              external: true,
              href: getMarketingHubPricingUrl()
            }
          }
        })
      };

    case AssetUninstallableReason.MISSING_MARKETPLACE_ASSET_ACCESS:
      return {
        hasAccess: false,
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystemMarketplaceCore.themes.installability.marketplaceAssetAccess"
        })
      };

    default:
      break;
  }

  return {
    hasAccess: true,
    message: portalId ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ecosystemMarketplaceCore.themes.installability.portalLicense",
      options: {
        offeringType: unescapedText(`ecosystemMarketplaceCore.offeringType.${offeringType}_lowercase`),
        portalId
      }
    }) : undefined
  };
}